import React, { useState, useEffect } from "react";
import { X, Upload } from "lucide-react";
import {
  Modal,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  FormHelperText,
} from "@mui/material";
import { dummyRoles } from "../../../../dummyData";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "90%", sm: 600 },
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: 0,
  maxHeight: "90vh",
  overflow: "auto",
};

const CreateUserModal = ({ open, handleClose, onSave, user }) => {
  const initialUserData = user || {
    name: "",
    email: "",
    password: "",
    role: "Inventory Manager",
    status: "active",
    phone: "",
  };

  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState(initialUserData);
  const [errors, setErrors] = useState({});
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Get available roles
  const availableRoles = dummyRoles.map(role => role.name);

  useEffect(() => {
    if (user) {
      setUserData(user);
    }
  }, [user]);

  const handleInputChange = e => {
    const { name, value } = e.target;
    setUserData({
      ...userData,
      [name]: value,
    });

    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const handleImageChange = e => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      setImage(selectedFile);
      const reader = new FileReader();
      reader.onload = event => {
        setImagePreview(event.target.result);
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const validateForm = () => {
    let formErrors = {};

    if (!userData.name) {
      formErrors.name = "Name is required";
    }

    if (!userData.email) {
      formErrors.email = "Email is required";
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(userData.email)
    ) {
      formErrors.email = "Invalid email address";
    }

    if (!userData.password) {
      formErrors.password = "Password is required";
    } else if (!userData.password.length < 6) {
      formErrors.password = "Password must be at least 6 characters";
    }

    if (!userData.role) {
      formErrors.role = "Role is required";
    }

    // Phone validation is optional
    if (userData.phone && !/^[+\d\s-]+$/.test(userData.phone)) {
      formErrors.phone = "Invalid phone number format";
    }

    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 800));

      // Create user data object with image
      const userDataWithImage = {
        ...userData,
        profileImage: imagePreview,
      };

      // Call the save function passed from parent
      onSave && onSave(userDataWithImage);

      // Reset form
      setUserData({
        name: "",
        email: "",
        password: "",
        role: "Inventory Manager",
        status: "active",
        phone: "",
      });
      setImage(null);
      setImagePreview(null);

      // Close modal
      handleClose();
    } catch (error) {
      console.error("Error creating user:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={loading ? null : handleClose}
      aria-labelledby="create-user-modal-title"
      aria-describedby="create-user-modal-description">
      <Box sx={style}>
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800 font-outfit">
            Create New User
          </h2>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-gray-500 hover:bg-gray-100 rounded-full p-2 transition-all">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left column - form fields */}
            <div className="space-y-4">
              <TextField
                fullWidth
                label="Name"
                name="name"
                variant="outlined"
                size="small"
                value={userData.name}
                onChange={handleInputChange}
                error={!!errors.name}
                helperText={errors.name}
                disabled={loading}
                InputProps={{
                  style: { fontFamily: "Outfit" },
                }}
                InputLabelProps={{
                  style: { fontFamily: "Outfit" },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#5652B0",
                    },
                  },
                }}
              />

              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                variant="outlined"
                size="small"
                value={userData.email}
                onChange={handleInputChange}
                error={!!errors.email}
                helperText={errors.email}
                disabled={loading}
                InputProps={{
                  style: { fontFamily: "Outfit" },
                }}
                InputLabelProps={{
                  style: { fontFamily: "Outfit" },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#5652B0",
                    },
                  },
                }}
              />

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={userData.phone}
                  onChange={handleInputChange}
                  placeholder="+91 98765 43210"
                  className={`w-full px-3 py-2 border ${
                    errors.phone ? "border-red-500" : "border-gray-300"
                  } rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-500">{errors.phone}</p>
                )}
              </div>

              <FormControl
                fullWidth
                size="small"
                error={!!errors.role}
                disabled={loading}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#5652B0",
                    },
                  },
                }}>
                <InputLabel style={{ fontFamily: "Outfit" }}>Role</InputLabel>
                <Select
                  name="role"
                  value={userData.role}
                  label="Role"
                  onChange={handleInputChange}
                  sx={{ fontFamily: "Outfit" }}>
                  {availableRoles.map(role => (
                    <MenuItem
                      key={role}
                      value={role}
                      sx={{ fontFamily: "Outfit" }}>
                      {role}
                    </MenuItem>
                  ))}
                </Select>
                {errors.role && <FormHelperText>{errors.role}</FormHelperText>}
              </FormControl>

              {!userData.password && (
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type="password"
                  variant="outlined"
                  size="small"
                  value={userData.password}
                  onChange={handleInputChange}
                  error={!!errors.password}
                  helperText={errors.password}
                  disabled={loading}
                  InputProps={{
                    style: { fontFamily: "Outfit" },
                  }}
                  InputLabelProps={{
                    style: { fontFamily: "Outfit" },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused fieldset": {
                        borderColor: "#5652B0",
                      },
                    },
                  }}
                />
              )}
            </div>

            {/* Right column - image upload */}
            <div className="flex flex-col items-center justify-start">
              <div
                className={`w-full h-48 border-2 border-dashed ${
                  errors.image ? "border-red-400" : "border-gray-300"
                } rounded-lg flex flex-col items-center justify-center cursor-pointer bg-gray-50 hover:bg-gray-100 transition-all`}>
                {imagePreview ? (
                  <div className="relative w-full h-full">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-full object-contain p-2"
                    />
                    <button
                      onClick={() => {
                        setImage(null);
                        setImagePreview(null);
                      }}
                      className="absolute top-2 right-2 bg-white p-1 rounded-full shadow-md hover:bg-gray-100">
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <label className="w-full h-full flex flex-col items-center justify-center cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageChange}
                      disabled={loading}
                    />
                    <Upload size={32} className="text-gray-400 mb-2" />
                    <p className="text-sm font-medium text-gray-500 font-outfit">
                      Upload Profile Image
                    </p>
                    <p className="text-xs text-gray-400 mt-1 font-outfit">
                      Click to browse
                    </p>
                  </label>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-2 font-outfit">
                *Preferred ratio - 1:1 (Square)
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-all font-outfit text-sm font-medium">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 bg-[#5652B0] text-white rounded-lg hover:bg-[#4A47A3] transition-all font-outfit text-sm font-medium flex items-center">
            {loading ? (
              <>
                <CircularProgress size={16} color="inherit" className="mr-2" />
                Creating...
              </>
            ) : (
              "Create User"
            )}
          </button>
        </div>
      </Box>
    </Modal>
  );
};

export default CreateUserModal;
